import React, { useState, useEffect } from "react";
import {
  AlertTriangle,
  Info,
  XCircle,
  ExternalLink,
  RefreshCw,
  Search,
  BookOpen,
  FileText,
} from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer/PageContainer";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { SearchBar } from "../components/composite/SearchBar/SearchBar";
import { Badge } from "../components/ui5/Badge";
import { EmptyState } from "../components/composite/EmptyState/EmptyState";
import { LoadingState } from "../components/composite/LoadingState/LoadingState";
import { MessageStrip } from "../components/ui/MessageStrip/MessageStrip";
import { ReleaseNoteModal } from "../components/features/ReleaseNoteModal/ReleaseNoteModal";
import { useCustomer } from "../contexts/CustomerContext";
import { changeImpactApi } from "../services/changeImpactService";
import { releaseNoteApi } from "../services/releaseNoteService";
import { processWhatsNewHtml } from "../utils/htmlProcessor";

/**
 * ChangeImpact Page
 * Shows impact of release changes on customer services
 * New Logic:
 * - Changed (related to customer) = HIGH
 * - Announcements (related to customer) = MEDIUM
 * - All non-related items = LOW
 * Shows ALL What's New entries in a structured way
 */
export default function ChangeImpact() {
  const { selectedCustomer } = useCustomer();
  const [searchQuery, setSearchQuery] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [releases, setReleases] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState("");
  const [releaseNote, setReleaseNote] = useState(null);
  const [showReleaseNoteModal, setShowReleaseNoteModal] = useState(false);

  // Fetch stored releases on mount
  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const response = await changeImpactApi.getStoredReleases();
        setReleases(response.data.data || []);
        setSelectedRelease(response.data.data?.[0] || ""); // Set latest as default
      } catch (err) {
        console.error("Error fetching releases:", err);
      }
    };
    fetchReleases();
  }, []);

  // Check for a SAP release note whenever the selected release changes
  // (most releases won't have one entered)
  useEffect(() => {
    if (!selectedRelease) {
      setReleaseNote(null);
      return;
    }

    let cancelled = false;
    releaseNoteApi
      .getByRelease(selectedRelease)
      .then((response) => {
        if (!cancelled) setReleaseNote(response.data?.data || null);
      })
      .catch((err) => {
        console.error("Error fetching release note:", err);
        if (!cancelled) setReleaseNote(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedRelease]);

  // Run change impact analysis
  const handleAnalyze = async () => {
    if (!selectedCustomer) {
      setError("Please select a customer first");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await changeImpactApi.analyzeChangeImpact(
        selectedCustomer.id,
        selectedRelease || null,
      );

      setReport(response.data.data);
    } catch (err) {
      console.error("Error analyzing change impact:", err);
      setError(
        err.response?.data?.message || "Failed to analyze change impact",
      );
    } finally {
      setLoading(false);
    }
  };

  // Filter impacts by search query and severity
  const filteredImpacts =
    report?.impacts.filter((impact) => {
      const matchesSearch =
        !searchQuery ||
        impact.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        impact.service_ids?.some((id) =>
          id.toLowerCase().includes(searchQuery.toLowerCase()),
        ) ||
        impact.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesSeverity =
        filterSeverity === "all" || impact.severity === filterSeverity;

      return matchesSearch && matchesSeverity;
    }) || [];

  // Get severity styles
  const getSeverityStyles = (severity) => {
    switch (severity) {
      case "HIGH":
        return {
          border: "border-l-4 border-l-red-500",
          bg: "bg-gradient-to-r from-red-50 to-white",
          icon: "text-red-600",
          badgeVariant: "destructive",
        };
      case "MEDIUM":
        return {
          border: "border-l-4 border-l-orange-500",
          bg: "bg-gradient-to-r from-orange-50 to-white",
          icon: "text-orange-600",
          badgeVariant: "default",
        };
      case "LOW":
        return {
          border: "border-l-4 border-l-yellow-500",
          bg: "bg-gradient-to-r from-yellow-50 to-white",
          icon: "text-yellow-600",
          badgeVariant: "secondary",
        };
      default:
        return {
          border: "border-l-4 border-l-gray-300",
          bg: "bg-white",
          icon: "text-gray-600",
          badgeVariant: "outline",
        };
    }
  };

  // Get icon for severity
  const getSeverityIcon = (severity) => {
    const styles = getSeverityStyles(severity);
    const iconClass = `h-6 w-6 ${styles.icon}`;

    switch (severity) {
      case "HIGH":
        return <AlertTriangle className={iconClass} />;
      case "MEDIUM":
        return <Info className={iconClass} />;
      case "LOW":
        return <XCircle className={iconClass} />;
      default:
        return <Info className={iconClass} />;
    }
  };

  if (loading) {
    return (
      <PageContainer
        title="Change Impact Analysis"
        description="Analyzing service changes..."
      >
        <LoadingState
          type="spinner"
          message="Analyzing service changes from SAP WhatsNew..."
        />
      </PageContainer>
    );
  }

  return (
    <>
    <PageContainer
      title="Change Impact Analysis"
      description={`Analyze Service ID Changes and Deprecations for ${
        selectedCustomer?.name || "selected customer"
      }`}
      actions={
        <div className="flex items-center gap-2">
          {/* Release Selector */}
          {releases.length > 0 && (
            <select
              value={selectedRelease}
              onChange={(e) => setSelectedRelease(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!selectedCustomer || loading}
            >
              {releases.map((release) => (
                <option key={release} value={release}>
                  Release {release}
                </option>
              ))}
            </select>
          )}

          <button
            onClick={handleAnalyze}
            disabled={!selectedCustomer || loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium text-sm leading-none">
              Analyze Changes
            </span>
          </button>
        </div>
      }
    >
      {/* Release Note CTA - only after a report has been loaded */}
      {report && releaseNote && (
        <MessageStrip design="Information" className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <span>
              There's a SAP release note for {selectedRelease} with manual
              activities, deprecations, and incompatible changes.
            </span>
            <button
              onClick={() => setShowReleaseNoteModal(true)}
              className="inline-flex items-center gap-1 font-medium text-blue-700 hover:text-blue-900 whitespace-nowrap"
            >
              <FileText className="w-4 h-4" />
              Check the Release Note
            </button>
          </div>
        </MessageStrip>
      )}

      {/* Error Message */}
      {error && (
        <MessageStrip design="Negative" className="mb-4">
          {error}
        </MessageStrip>
      )}

      {/* Results Summary */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-sm text-gray-600 mb-1">Selected Services</div>
            <div className="text-2xl font-bold text-gray-900">
              {report.total_services}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600 mb-1">Changes</div>
            <div className="text-2xl font-bold text-blue-600">
              {report.impacted_services}
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-red-500">
            <div className="text-sm text-gray-600 mb-1">High</div>
            <div className="text-2xl font-bold text-red-600">
              {report.summary.high}
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-orange-500">
            <div className="text-sm text-gray-600 mb-1">Medium</div>
            <div className="text-2xl font-bold text-orange-600">
              {report.summary.medium}
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-yellow-500">
            <div className="text-sm text-gray-600 mb-1">Low</div>
            <div className="text-2xl font-bold text-yellow-600">
              {report.summary.low}
            </div>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      {report && report.impacts.length > 0 && (
        <div className="mb-6 space-y-4">
          <SearchBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by service ID, title, or description..."
            onClear={() => setSearchQuery("")}
          />

          <div className="flex gap-4 items-center">
            <label className="text-sm font-medium text-gray-700">
              Filter by Severity:
            </label>
            <select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Severities</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>
        </div>
      )}

      {/* Impact Cards */}
      {report && filteredImpacts.length > 0 ? (
        <div className="grid gap-6">
          {filteredImpacts.map((impact, index) => {
            const severityStyles = getSeverityStyles(impact.severity);

            return (
              <Card
                key={index}
                className={`p-6 hover:shadow-lg transition-all ${severityStyles.border} ${severityStyles.bg}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getSeverityIcon(impact.severity)}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {impact.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {impact.service_ids.join(", ")}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant={severityStyles.badgeVariant}>
                      {impact.severity}
                    </Badge>
                    {impact.change_type && (
                      <Badge variant="outline">{impact.change_type}</Badge>
                    )}
                    {impact.is_deprecation && (
                      <Badge variant="destructive">Deprecation</Badge>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="text-gray-700 mb-4">
                  {impact.raw_data?.Description ? (
                    <div
                      className="leading-relaxed prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: processWhatsNewHtml(
                          impact.raw_data.Description,
                          impact.raw_data.outputloio,
                        ),
                      }}
                    />
                  ) : (
                    <p className="leading-relaxed">{impact.description}</p>
                  )}
                </div>

                {/* Metadata */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">
                        Valid as of:
                      </span>
                      <span className="ml-2 text-gray-600">
                        {impact.valid_as_of}
                      </span>
                    </div>
                    {impact.environment && impact.environment.length > 0 && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Environment:
                        </span>
                        <span className="ml-2 text-gray-600">
                          {impact.environment.join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : report && report.impacts.length === 0 ? (
        <EmptyState
          icon={<Search className="h-12 w-12" />}
          title="No changes detected"
          description={`No service changes detected for ${selectedCustomer?.name} in release ${report.release_version}.`}
        />
      ) : !report ? (
        <EmptyState
          icon={<AlertTriangle className="h-12 w-12" />}
          title="Ready to analyze"
          description={
            selectedCustomer
              ? `Click "Analyze Changes" to check for service updates affecting ${selectedCustomer.name}.`
              : 'Select a customer and click "Analyze Changes" to get started.'
          }
          actionLabel={selectedCustomer ? "Analyze Changes" : undefined}
          onAction={selectedCustomer ? handleAnalyze : undefined}
        />
      ) : (
        <EmptyState
          icon={<Search className="h-12 w-12" />}
          title="No results found"
          description={`No impacts match "${searchQuery}". Try adjusting your search or filters.`}
          actionLabel="Clear Search"
          onAction={() => setSearchQuery("")}
        />
      )}
    </PageContainer>

    <ReleaseNoteModal
      open={showReleaseNoteModal}
      onClose={() => setShowReleaseNoteModal(false)}
      note={releaseNote}
    />
    </>
  );
}
